import { type ClipboardEvent, type FormEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { type AttachmentDTO, DRAFT_SAVE_MS, type DraftDTO, draftKey as serverDraftKey, MAX_ATTACHMENTS } from "@justone/shared";
import { composeKeys, type DraftPlace, useDrafts, useSaveDraft, useScheduled, useScheduleMutations } from "../api/compose";
import { errorMessage, http } from "../api/http";
import { pendingMessage, useMe, useSendMessage, useUsers } from "../api/queries";
import { formatWhen } from "../lib/format";
import { uploadAttachment } from "../api/upload";
import type { RoomView } from "../lib/room";
import { fromBody, type TaggedText } from "../lib/mentionTags";
import { formatBytes } from "./Attachments";
import { MentionList, MentionTextarea, useMentionField } from "./MentionField";
import { useMenuKeys } from "./Popover";
import { ScheduleDialog, schedulePresets } from "./ScheduleDialog";
import { Icon, useDismiss, useToast } from "./ui";

interface Upload {
  key: string;
  name: string;
  size: number;
  progress: number;
  attachment?: AttachmentDTO;
  error?: string;
  abort?: () => void;
}

/** 채널·스레드를 오가도 쓰던 글(멘션 태그 포함)이 남도록 임시 저장 */
const drafts = new Map<string, TaggedText>();
const EMPTY: TaggedText = { text: "", tags: [] };

const isTouch = () => window.matchMedia("(pointer: coarse)").matches;
const isComposingKey = (e: KeyboardEvent) => e.nativeEvent.isComposing || e.keyCode === 229;

interface Props {
  room: RoomView;
  /** 스레드 답글 입력창이면 원글 id */
  parentId?: number;
  placeholder?: string;
  autoFocus?: boolean;
}

export function Composer({ room, parentId, placeholder, autoFocus = !isTouch() }: Props) {
  const draftKey = `${room.ref.kind}:${room.ref.id}:${parentId ?? ""}`;
  const place: DraftPlace = room.ref.kind === "channel" ? { channelId: room.ref.id, parentId } : { conversationId: room.ref.id, parentId };
  const qc = useQueryClient();
  const { data: users = [] } = useUsers();
  // 처음 글: 이 화면에서 쓰던 글, 없으면 서버에 저장된 초안 (다른 기기에서 쓴 것도)
  const serverDraft = () => qc.getQueryData<DraftDTO[]>(composeKeys.drafts)?.find((d) => d.key === serverDraftKey(place));
  const field = useMentionField(drafts.get(draftKey) ?? (serverDraft() ? fromBody(serverDraft()!.body, users) : EMPTY), (v) => (v.text ? drafts.set(draftKey, v) : drafts.delete(draftKey)));
  const text = field.value.text;
  const [alsoInChannel, setAlsoInChannel] = useState(false);
  const [silentMenu, setSilentMenu] = useState(false);
  const closeSilent = useCallback(() => setSilentMenu(false), []);
  const sendRef = useDismiss<HTMLDivElement>(silentMenu, closeSilent);
  useMenuKeys(silentMenu, sendRef);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const ref = field.ref;
  const formRef = useRef<HTMLFormElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const send = useSendMessage(room.ref);
  const me = useMe();
  const toast = useToast();
  const navigate = useNavigate();
  const scheduleOps = useScheduleMutations();
  const [pickingTime, setPickingTime] = useState(false);
  const { data: scheduledAll = [] } = useScheduled();
  const scheduledHere = scheduledAll.filter((s) => (place.channelId ? s.channelId === place.channelId : s.conversationId === place.conversationId) && (s.parentId ?? undefined) === parentId);

  /* 초안: 입력을 멈추고 잠시 뒤 서버에 저장 (보내지 않고 떠나도 초안 탭에 남고, 다른 기기에서 이어 쓴다) */
  const { data: draftList } = useDrafts();
  const saveDraft = useSaveDraft();
  const savedBody = useRef<string | null>(null);
  const touched = useRef(text.length > 0);
  useEffect(() => {
    if (!draftList) return;
    const d = draftList.find((x) => x.key === serverDraftKey(place));
    if (savedBody.current === null) savedBody.current = d?.body ?? "";
    // 서버 초안이 늦게 왔고 아직 아무것도 쓰지 않았으면 채운다
    if (d && !touched.current && !field.value.text) {
      field.reset(fromBody(d.body, users));
      savedBody.current = d.body;
    }
  }, [draftList]); // eslint-disable-line react-hooks/exhaustive-deps -- 초안 목록이 올 때만
  const pendingDraft = useRef<{ timer: ReturnType<typeof setTimeout>; body: string } | null>(null);
  const flushDraft = () => {
    const p = pendingDraft.current;
    if (!p) return;
    clearTimeout(p.timer);
    pendingDraft.current = null;
    savedBody.current = p.body;
    saveDraft.mutate({ ...place, body: p.body });
  };
  useEffect(() => {
    if (text) touched.current = true;
    const body = field.body();
    if (savedBody.current === null || body.trim() === savedBody.current.trim()) {
      if (pendingDraft.current) {
        clearTimeout(pendingDraft.current.timer);
        pendingDraft.current = null;
      }
      return;
    }
    if (pendingDraft.current) clearTimeout(pendingDraft.current.timer);
    pendingDraft.current = { body, timer: setTimeout(flushDraft, DRAFT_SAVE_MS) };
  }, [field.value]); // eslint-disable-line react-hooks/exhaustive-deps -- 글이 바뀔 때만
  // 다른 채널로 옮기면 기다리지 않고 저장한다
  useEffect(() => flushDraft, []); // eslint-disable-line react-hooks/exhaustive-deps -- 떠날 때만
  const id = parentId ? `composer-thread-${parentId}` : "composer";
  const label = placeholder ?? room.placeholder;

  const patchUpload = (key: string, patch: Partial<Upload>) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  const addFiles = (files: File[]) => {
    const room = MAX_ATTACHMENTS - uploads.length;
    if (files.length > room) toast(`파일은 한 번에 ${MAX_ATTACHMENTS}개까지 보낼 수 있습니다.`);
    for (const file of files.slice(0, Math.max(0, room))) {
      const key = `${Date.now()}-${Math.random()}`;
      const job = uploadAttachment(file, (progress) => patchUpload(key, { progress }));
      setUploads((list) => [...list, { key, name: file.name, size: file.size, progress: 0, abort: job.abort }]);
      job.promise.then(
        (attachment) => patchUpload(key, { attachment, progress: 1, abort: undefined }),
        (err) => patchUpload(key, { error: errorMessage(err), abort: undefined }),
      );
    }
    ref.current?.focus();
  };

  const removeUpload = (u: Upload) => {
    u.abort?.();
    if (u.attachment) void http.delete(`/api/attachments/${u.attachment.id}`).catch(() => {});
    setUploads((list) => list.filter((x) => x.key !== u.key));
  };

  // 채널 화면(또는 스레드 창)에 파일을 끌어다 놓으면 첨부
  useEffect(() => {
    const zone = formRef.current?.closest<HTMLElement>(".channel-col, .thread-panel");
    if (!zone) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes("Files");
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth++;
      zone.classList.add("dropping");
    };
    const onLeave = () => {
      if (--depth <= 0) zone.classList.remove("dropping");
    };
    const onOver = (e: DragEvent) => hasFiles(e) && e.preventDefault();
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      zone.classList.remove("dropping");
      addFiles([...(e.dataTransfer?.files ?? [])]);
    };
    zone.addEventListener("dragenter", onEnter);
    zone.addEventListener("dragleave", onLeave);
    zone.addEventListener("dragover", onOver);
    zone.addEventListener("drop", onDrop);
    return () => {
      zone.removeEventListener("dragenter", onEnter);
      zone.removeEventListener("dragleave", onLeave);
      zone.removeEventListener("dragover", onOver);
      zone.removeEventListener("drop", onDrop);
    };
  });

  /** 스크린샷 등 붙여넣은 파일 */
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = [...e.clipboardData.files];
    if (files.length === 0) return;
    e.preventDefault();
    addFiles(files);
  };

  const uploading = uploads.some((u) => !u.attachment && !u.error);
  const ready = uploads.filter((u) => u.attachment);
  const canSend = (text.trim().length > 0 || ready.length > 0) && !uploading;

  const submit = (opts: { silent?: boolean } = {}) => {
    const body = field.body().trim();
    setSilentMenu(false);
    if (!canSend) {
      if (uploading) toast("파일을 다 올린 뒤에 보낼 수 있습니다.");
      return;
    }
    const sent = uploads;
    const written = field.value;
    // 보내면 서버가 이곳의 초안을 지운다
    savedBody.current = "";
    field.reset(EMPTY);
    setUploads([]);
    const input = { body, attachmentIds: ready.map((u) => u.attachment!.id), parentId, alsoInChannel: parentId ? alsoInChannel : undefined, silent: opts.silent || undefined };
    // 응답을 기다리지 않고 바로 목록에 보인다 (보내는 중). 연달아 보내도 된다
    send.mutate(
      { input, pending: pendingMessage(room.ref, me.id, input, ready.map((u) => u.attachment!)) },
      {
        onSuccess: () => opts.silent && toast("알림 없이 보냈습니다."),
        onError: (err) => {
          // 실패하면 쓴 글(태그 포함)과 첨부를 되돌려 놓는다.
          if (!field.body()) field.reset(written);
          setUploads((current) => (current.length ? current : sent));
          toast(`보내지 못했습니다: ${errorMessage(err)}`);
        },
      },
    );
    ref.current?.focus();
  };

  /** 예약: 정한 시각에 서버가 보낸다. 붙인 파일도 함께 */
  const schedule = (at: Date) => {
    setSilentMenu(false);
    setPickingTime(false);
    if (!canSend) {
      if (uploading) toast("파일을 다 올린 뒤에 예약할 수 있습니다.");
      return;
    }
    const body = field.body().trim();
    const written = field.value;
    const sent = uploads;
    // 보내기처럼 바로 비운다 (응답을 기다리는 사이 새로 쓴 글이 지워지지 않게). 예약하면 서버가 이곳의 초안을 지운다
    savedBody.current = "";
    field.reset(EMPTY);
    setUploads([]);
    scheduleOps.create.mutate(
      { ...place, body, attachmentIds: ready.map((u) => u.attachment!.id), alsoInChannel: parentId ? alsoInChannel : undefined, sendAt: at.toISOString() },
      {
        onSuccess: (s) => toast(`${formatWhen(s.sendAt)}에 보냅니다.`, { label: "예약 보기", run: () => navigate("/drafts") }),
        onError: (err) => {
          // 실패하면 쓴 글(태그 포함)과 첨부를 되돌려 놓는다
          if (!field.body()) field.reset(written);
          setUploads((current) => (current.length ? current : sent));
          toast(`예약하지 못했습니다: ${errorMessage(err)}`);
        },
      },
    );
    ref.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isComposingKey(e) || field.onKeyDown(e)) return;
    if (e.key !== "Enter" || e.shiftKey) return;
    // 휴대폰에서는 Enter가 줄바꿈, 전송은 버튼으로
    if (isTouch()) return;
    e.preventDefault();
    submit({ silent: e.altKey });
  };

  return (
    <>
      <form
        ref={formRef}
        className={`composer${parentId ? " composer-thread" : ""}`}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          submit();
        }}
      >
        <MentionList field={field} />
        {scheduledHere.length > 0 && (
          <button type="button" className={`scheduled-bar${scheduledHere.some((x) => x.status === "failed") ? " failed" : ""}`} onClick={() => navigate("/drafts")}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            {scheduledHere.some((x) => x.status === "failed")
              ? "보내지 못한 예약 메시지가 있습니다"
              : `예약된 메시지 ${scheduledHere.length}개 · 다음 ${formatWhen(scheduledHere[0]!.sendAt)}`}
          </button>
        )}
        {uploads.length > 0 && (
          <ul className="upload-list" aria-label="첨부할 파일">
            {uploads.map((u) => (
              <li key={u.key} className={u.error ? "error" : u.attachment ? "done" : "busy"}>
                {u.attachment?.thumbUrl ? <img src={u.attachment.thumbUrl} alt="" /> : <span className="ext">{u.name.split(".").pop()?.slice(0, 4).toUpperCase()}</span>}
                <span className="meta">
                  <span className="name">{u.name}</span>
                  <span className="size">{u.error ?? (u.attachment ? formatBytes(u.size) : `${Math.round(u.progress * 100)}%`)}</span>
                </span>
                {!u.attachment && !u.error && <span className="bar" style={{ width: `${u.progress * 100}%` }} />}
                <button type="button" className="icon-btn" onClick={() => removeUpload(u)} aria-label={`${u.name} 빼기`}>
                  <Icon name="close" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="composer-box">
          <button type="button" className="icon-btn attach" onClick={() => fileRef.current?.click()} aria-label="파일 첨부" data-tip="파일 첨부 · 끌어다 놓거나 붙여넣어도 됩니다">
            <Icon name="clip" />
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              addFiles([...(e.target.files ?? [])]);
              e.target.value = "";
            }}
          />
          <label htmlFor={id} className="sr-only">
            {label}
          </label>
          <MentionTextarea field={field} id={id} rows={1} placeholder={label} onKeyDown={onKeyDown} onPaste={onPaste} maxLength={8000} autoFocus={autoFocus} />
          <div className="send-group" ref={sendRef}>
            <button type="submit" className="btn btn-primary btn-sm send" disabled={!canSend} aria-label="보내기" aria-keyshortcuts="Enter" data-tip="보내기" data-kbd="Enter">
              <Icon name="send" />
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm send-more"
              disabled={!canSend}
              aria-label="보내기 옵션"
              data-tip="알림 없이 보내기 · 예약 전송"
              aria-haspopup="menu"
              aria-expanded={silentMenu}
              onClick={() => setSilentMenu((v) => !v)}
            >
              <Icon name="chevronUp" />
            </button>
            {silentMenu && (
              <div className="menu send-menu" role="menu" aria-label="보내기 옵션">
                <button type="button" role="menuitem" onClick={() => submit({ silent: true })}>
                  알림 없이 보내기
                  <span className="sub">다른 사람에게 알림이 가지 않습니다 (Alt+Enter)</span>
                </button>
                <hr className="menu-sep" />
                <div className="menu-label" role="presentation">
                  예약 전송
                </div>
                {schedulePresets().map((p) => (
                  <button key={p.label} type="button" role="menuitem" onClick={() => schedule(p.at)}>
                    {p.label}
                    <span className="sub">{formatWhen(p.at.toISOString())}</span>
                  </button>
                ))}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setSilentMenu(false);
                    setPickingTime(true);
                  }}
                >
                  날짜와 시간 고르기…
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="composer-hint">
          {parentId ? (
            <label className="also-channel">
              <input type="checkbox" checked={alsoInChannel} onChange={(e) => setAlsoInChannel(e.target.checked)} />
              {room.alsoLabel}
            </label>
          ) : (
            <span>**굵게** *기울임* `코드` 같은 마크다운, @로 멘션</span>
          )}
          <span className="keys">Enter로 보내기, Shift+Enter로 줄바꿈</span>
        </div>
      </form>
      {/* 입력창 form 밖에 (form 안에 form을 두면 예약이 그냥 보내기가 된다) */}
      {pickingTime && <ScheduleDialog onPick={schedule} onClose={() => setPickingTime(false)} />}
    </>
  );
}
